<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

import { TriangleAlert } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { PlusCircle, Folders, Globe, Radar, Package } from 'lucide-vue-next'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FormCreateOrder, FormCreatePlanetVarLayer, FormCreateRadar, FormCreateCollectedData } from '@/components/layers'
import FeatureSelection from './FeatureSelection.vue'

const emit = defineEmits(['formSubmitted'])
const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  org: {
    type: Object,
    required: true
  },
})

const productGroups = [
  {
    label: 'EarthPBC',
    products: [
      {
        label: 'Collected Data',
        value: 'collectedData',
        aoiRequirement: 'not_required',
        component: FormCreateCollectedData,
        icon: Folders,
        color: '#FF9900',
        type: 4
      }
    ]
  },
  {
    label: 'Planet',
    products: [
      {
        label: 'Order Scene',
        value: 'order-scene',
        aoiRequirement: 'required',
        component: FormCreateOrder,
        icon: Package,
        color: '#4CAF50',
        type: 1
      },
      {
        label: 'Planetary Variables',
        value: 'planetVars',
        aoiRequirement: 'required',
        component: FormCreatePlanetVarLayer,
        icon: Globe,
        color: '#7ED321',
        type: 2
      }
    ]
  },
  {
    label: 'Protected Seas',
    products: [
      {
        label: 'Radar',
        value: 'radar',
        aoiRequirement: 'not_required',
        component: FormCreateRadar,
        icon: Radar,
        color: '#1A73E8',
        type: 3
      },
    ]
  }
]

const allProducts = productGroups.flatMap(group =>
  group.products.map(product => ({...product, group: group.label}))
)

const selectedOrganizationId = ref(props.org.organization_id.toString())
const selectedProductValue = ref(allProducts[0]?.value)
const selectedProduct = computed(() => {
  return allProducts.find(product => product.value === selectedProductValue.value)
})

const layerName = ref('')
const selectedGeometry = ref(null)
const isOpen = ref(false)
const showOptionalAOI = ref(false)

const onFormSubmitted = (newLayer) => {
  emit('formSubmitted', newLayer)
  isOpen.value = false
}
</script>

<template>
<div>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger>
      <slot>
        <Button class="px-2" variant="secondary" size="sm">
          <div class="flex items-center justify-between">
            <PlusCircle class="text-gray-500 w-4 h-4 mr-1" />
            Add Layer
          </div>
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[400px] max-h-[80vh] overflow-auto xl:w-[400px] m-3" no-overlay to="#mapSection">
      <DialogHeader>
        <DialogTitle>New layer</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      <div class="w-full overflow-x-auto">
        <div class="mb-3">
          <Label class="mb-1">Name</Label>
          <Input v-model="layerName" placeholder="Layer Name" />
        </div>

        <div class="mb-5">
          <Label class="mb-1">Data Source</Label>
          <Select v-model="selectedProductValue">
            <SelectTrigger>
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent class="max-h-[300px]">
              <SelectGroup v-for="group in productGroups" :key="group.label">
                <SelectLabel>{{ group.label }}</SelectLabel>
                <SelectItem
                  v-for="product in group.products"
                  :key="product.value"
                  :value="product.value"
                >
                  <div class="flex items-center">
                    <component :is="product.icon" class="w-4 h-4 mr-2" :style="{ color: product.color }" />
                    {{ product.label }}
                  </div>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <!-- AOI Section -->
        <div v-if="selectedProduct?.aoiRequirement === 'required' || (selectedProduct?.aoiRequirement === 'optional' && showOptionalAOI)" :key="`feature-${selectedProductValue}`" class="mt-4">
          <Label class="mb-1">AOI <span class="text-gray-500">(Area of Interest)</span></Label>
          <FeatureSelection v-model:selectedGeometry="selectedGeometry" />

          <div class="mt-4" v-if="selectedProduct?.aoiRequirement === 'required' && (!selectedOrganizationId || !selectedGeometry)" :key="`warning-${selectedProductValue}`">
            <p class="flex items-center text-amber-500 bg-yellow-50 border-amber-100 border rounded p-2">
              <TriangleAlert class="w-5 h-5 mr-2" />
              Select AOI to continue
            </p>
          </div>
        </div>

        <!-- Add AOI Button for Optional Products -->
        <div v-if="selectedProduct?.aoiRequirement === 'optional' && !showOptionalAOI" class="mt-4">
          <Button
            variant="secondary"
            size="sm"
            @click="showOptionalAOI = true"
          >
            <PlusCircle class="w-4 h-4 mr-2" />
            AOI (Area of Interest)
          </Button>
        </div>

        <!-- Component Section -->
        <div
          class="mt-4"
          v-if="selectedProduct?.component && (
            selectedProduct?.aoiRequirement === 'not_required' ||
            (selectedProduct?.aoiRequirement === 'required' && selectedGeometry) ||
            (selectedProduct?.aoiRequirement === 'optional')
          )"
          :key="`component-${selectedProductValue}-${!!selectedGeometry}`"
        >
          <component
            :is="selectedProduct.component"
            :feature="selectedGeometry"
            :orgId="selectedOrganizationId"
            :name="layerName"
            @formSubmitted="onFormSubmitted($event)"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</div>
</template>
